'use client';

import React, { type ReactNode, useEffect, useRef, useState } from 'react';
import { cn } from '@/utils/tailwind.util';
import { TabsList, TabsRoot } from './tabsRoot';
import { TabsTrigger, type TabsTriggerProps } from './TabsTrigger';
export interface TabsWithSliderProps<T extends string = string> {
  isFull: boolean;
  size: TabsTriggerProps['size'];
  value: T;
  onValueChange: (value: T) => void;
  options: {
    value: T;
    label: ReactNode;
  }[];
  className?: string;
  tabTriggerProps?: Pick<TabsTriggerProps, 'className'>;
}
export const TabsWithSlider = <T extends string,>({
  isFull,
  value,
  options,
  onValueChange,
  size,
  className,
  tabTriggerProps
}: TabsWithSliderProps<T> & {
  tabTriggerProps?: Pick<TabsTriggerProps, 'className' | 'asChild'>;
}) => {
  const [sliderStyle, setSliderStyle] = useState<React.CSSProperties>({});
  const [enableTransition, setEnableTransition] = useState(false);
  const tabsListRef = useRef<HTMLDivElement>(null);
  const {
    className: tabTriggerClassName,
    ...restTabTriggerProps
  } = tabTriggerProps || {};
  useEffect(() => {
    const tabsList = tabsListRef.current;
    if (!tabsList) return;
    const updateSliderPosition = () => {
      const activeTab = tabsList.querySelector('[data-state="active"]') as HTMLElement;
      if (!activeTab) return;
      const computedTabsListStyle = window.getComputedStyle(tabsList);
      const tabsListWidth = Number(computedTabsListStyle.width.replace('px', ''));
      const calculatedSliderWidth = tabsListWidth / options.length;
      const activeTabIndex = options.findIndex(option => option.value === value);
      setSliderStyle({
        transform: `translateX(${calculatedSliderWidth * activeTabIndex}px)`,
        width: `${calculatedSliderWidth}px`
      });
    };

    // Set initial position
    updateSliderPosition();

    // Watch for tab state changes
    const observer = new MutationObserver(updateSliderPosition);
    tabsList.querySelectorAll('[role="tab"]').forEach(tab => {
      observer.observe(tab, {
        attributes: true,
        attributeFilter: ['data-state']
      });
    });

    // Watch for resize
    const resizeObserver = new ResizeObserver(updateSliderPosition);
    resizeObserver.observe(tabsList);
    return () => {
      observer.disconnect();
      resizeObserver.disconnect();
    };
  }, [value, options]);
  const handleTabClick = (newValue: string) => {
    setEnableTransition(true);
    onValueChange(newValue as T);
  };
  return <TabsRoot value={value} onValueChange={handleTabClick} data-sentry-element="TabsRoot" data-sentry-component="TabsWithSlider" data-sentry-source-file="TabsWithSlider.tsx">
      <TabsList className={cn('relative w-full', className)} data-sentry-element="TabsList" data-sentry-source-file="TabsWithSlider.tsx">
        <div className="relative flex w-full" ref={tabsListRef}>
          {/* Background slider */}
          <div className={cn('absolute inset-0 w-[50%] rounded-xs ease-out', enableTransition && 'transition-transform duration-200')} style={sliderStyle}>
            <div className="h-full w-full rounded-sm bg-white shadow-sm" />
          </div>

          {options.map(option => <TabsTrigger {...restTabTriggerProps} key={option.value} value={option.value} size={size} className={cn('relative z-10 border-0 bg-transparent cursor-pointer max-h-none p-0', isFull && 'flex-1', tabTriggerClassName)}>
              {option.label}
            </TabsTrigger>)}
        </div>
      </TabsList>
    </TabsRoot>;
};